import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertCircle } from 'lucide-react';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/api';
import { ADMIN_GOOGLE_STATE_KEY, oauthStateMatches } from './admin/auth/googleSignIn';

export const AdminGoogleCallback: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  // The state check is single-use, so a callback must be handled exactly once per mount
  // (a second run would find the state already cleared and fail a genuine sign-in).
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const handleCallback = async () => {
      try {
        // Extract authorization code from URL
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const error = params.get('error');

        if (error) {
          throw new Error(`Google OAuth error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received from Google');
        }

        // The state must be the one THIS browser generated when sign-in started. It is
        // single-use: cleared before the check, so a replayed callback also fails.
        const expectedState = await storage.getItem(ADMIN_GOOGLE_STATE_KEY);
        await storage.removeItem(ADMIN_GOOGLE_STATE_KEY);
        if (!oauthStateMatches(expectedState, params.get('state'))) {
          throw new Error('This sign-in could not be verified. Please start again from the sign-in page.');
        }

        // Send authorization code to backend (backend handles token exchange securely).
        // The backend decides who may sign in: a verified coachcedar.com Google account
        // with an active, pre-created Admin Portal account. Nothing is decided here.
        const authResponse = await fetch(`${API_BASE_URL}/api/admin/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            code,
            redirect_uri: `${window.location.origin}/admin/auth/google/callback`
          }),
        });

        if (!authResponse.ok) {
          let detail = '';
          try {
            const errorData = await authResponse.json();
            detail = typeof errorData?.detail === 'string' ? errorData.detail : '';
          } catch {
            // non-JSON error body
          }
          throw new Error(detail || 'Authentication failed');
        }

        // Same shape as the password login: { success, admin: { admin_id, email,
        // full_name, role, permissions, token, expires_at, auth_method } }.
        const authData = await authResponse.json();
        const admin = authData?.admin;
        if (!authData?.success || !admin?.token) {
          throw new Error('Invalid response from server');
        }

        // Store admin session (using admin-specific storage keys). The role is the
        // server's — the account's own role, never assumed.
        await storage.setItem('admin_token', admin.token);
        await storage.setItem('admin_user', JSON.stringify({
          admin_id: admin.admin_id,
          email: admin.email,
          full_name: admin.full_name,
          role: admin.role,
          auth_method: 'google'
        }));

        setStatus('success');
        
        // Small delay to show success state, then navigate
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 500);

      } catch (err: any) {
        console.error('Admin Google OAuth failed');
        setStatus('error');
        setErrorMessage(err.message || 'Authentication failed');
        
        // Redirect back to login after 3 seconds
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
      }
    };

    handleCallback();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--cedar-subtle)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'var(--cedar-primary)' }}>
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Coach Cedar Admin</h1>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          {status === 'processing' && (
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 mb-4" style={{ borderColor: 'var(--cedar-primary)' }}></div>
              <p className="text-gray-700 font-medium">Authenticating with Google...</p>
              <p className="text-gray-500 text-sm mt-2">Please wait</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{ backgroundColor: 'var(--cedar-subtle)' }}>
                <svg className="w-6 h-6" style={{ color: 'var(--cedar-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-700 font-medium">Authentication successful!</p>
              <p className="text-gray-500 text-sm mt-2">Redirecting to dashboard...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-red-700 font-medium">Authentication Failed</p>
              <p className="text-red-600 text-sm mt-2">{errorMessage}</p>
              <p className="text-gray-500 text-xs mt-4">Redirecting back to login...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
